/**
 * Plantafel Grid Time Utilities - Grid Position to DateTime Mapping
 *
 * This module provides the core functions for converting between:
 * - Grid positions (dayIndex + minutesInWindow) and
 * - ISO datetime strings
 *
 * Key concept:
 * - dayIndex (0-6) determines the DATE
 * - minutesInWindow determines the TIME within day_start...day_end
 *
 * This separation ensures multi-day shifts are handled correctly.
 */

import { OperationsSettings } from '../services/PlantafelService';

const DEFAULT_SETTINGS: Pick<OperationsSettings, 'day_start_minutes' | 'day_end_minutes' | 'snap_minutes'> = {
  day_start_minutes: 6 * 60,  // 06:00
  day_end_minutes: 18 * 60,   // 18:00
  snap_minutes: 30,
};

export interface GridPosition {
  dayIndex: number;           // 0-6 (Monday to Sunday)
  minutesInWindow: number;    // Minutes within day_start...day_end
}

export interface GridSettings {
  dayStartMinutes: number;
  dayEndMinutes: number;
  snapMinutes: number;
}

/**
 * Gets grid settings from OperationsSettings or uses defaults.
 */
export function getGridSettings(settings?: OperationsSettings): GridSettings {
  return {
    dayStartMinutes: settings?.day_start_minutes ?? DEFAULT_SETTINGS.day_start_minutes,
    dayEndMinutes: settings?.day_end_minutes ?? DEFAULT_SETTINGS.day_end_minutes,
    snapMinutes: settings?.snap_minutes ?? DEFAULT_SETTINGS.snap_minutes,
  };
}

/**
 * Gets the work window duration in minutes.
 */
export function getWorkWindowMinutes(gridSettings: GridSettings): number {
  return gridSettings.dayEndMinutes - gridSettings.dayStartMinutes;
}

/**
 * Gets the day index (0-6) from an X pixel position.
 * @param xPx - X position in pixels from container start (including scroll)
 * @param colWidth - Width of each day column in pixels
 */
export function getDayIndexFromX(xPx: number, colWidth: number): number {
  const dayIndex = Math.floor(xPx / colWidth);
  return Math.max(0, Math.min(6, dayIndex));
}

/**
 * Gets the minutes within the work window from X position within a day column.
 * @param xPxWithinDay - X position within the day column
 * @param colWidth - Width of the day column
 * @param gridSettings - Grid settings with day boundaries
 * @returns Minutes offset from day start (day_start_minutes)
 */
export function getMinutesInWindowFromX(
  xPxWithinDay: number,
  colWidth: number,
  gridSettings: GridSettings
): number {
  const workWindowMinutes = getWorkWindowMinutes(gridSettings);
  const ratio = xPxWithinDay / colWidth;
  const rawMinutes = ratio * workWindowMinutes;

  // Snap to grid
  const snapped = Math.round(rawMinutes / gridSettings.snapMinutes) * gridSettings.snapMinutes;

  // Clamp to work window
  return Math.max(0, Math.min(workWindowMinutes, snapped));
}

/**
 * Converts grid position to ISO datetime string.
 * This is the core function for converting drop/resize positions to datetime.
 *
 * @param dayIndex - Day index (0-6)
 * @param minutesInWindow - Minutes within work window (0 = day_start)
 * @param weekStart - Week start date (Monday)
 * @param gridSettings - Grid settings
 * @returns ISO datetime string in local timezone
 */
export function gridToDateTime(
  dayIndex: number,
  minutesInWindow: number,
  weekStart: Date,
  gridSettings: GridSettings
): string {
  const targetDate = new Date(weekStart);
  targetDate.setDate(targetDate.getDate() + dayIndex);

  // minutesInWindow is offset from day_start_minutes
  const totalMinutesFromMidnight = gridSettings.dayStartMinutes + minutesInWindow;
  const hours = Math.floor(totalMinutesFromMidnight / 60);
  const minutes = totalMinutesFromMidnight % 60;

  targetDate.setHours(hours, minutes, 0, 0);

  return formatLocalDateTime(targetDate);
}

/**
 * Converts an ISO datetime string to grid position.
 * Inverse of gridToDateTime.
 *
 * @param isoString - ISO datetime string
 * @param weekStart - Week start date (Monday)
 * @param gridSettings - Grid settings
 * @returns Grid position with dayIndex and minutesInWindow
 */
export function dateTimeToGrid(
  isoString: string,
  weekStart: Date,
  gridSettings: GridSettings
): GridPosition {
  const date = new Date(isoString);
  const weekStartNormalized = new Date(weekStart);
  weekStartNormalized.setHours(0, 0, 0, 0);

  // Calculate day index
  const dayDiff = Math.floor(
    (date.getTime() - weekStartNormalized.getTime()) / (1000 * 60 * 60 * 24)
  );
  const dayIndex = Math.max(0, Math.min(6, dayDiff));

  // Calculate minutes from midnight
  const minutesFromMidnight = date.getHours() * 60 + date.getMinutes();

  // Calculate minutes within work window
  const minutesInWindow = minutesFromMidnight - gridSettings.dayStartMinutes;

  return {
    dayIndex,
    minutesInWindow: Math.max(0, minutesInWindow),
  };
}

/**
 * Converts X pixel position to full datetime, handling all snapping and clamping.
 *
 * @param xPx - X position in pixels (including scroll offset)
 * @param colWidth - Width of each day column
 * @param weekStart - Week start date
 * @param gridSettings - Grid settings
 * @param isEndTime - If true, applies end-time snapping rules
 */
export function pixelToDateTime(
  xPx: number,
  colWidth: number,
  weekStart: Date,
  gridSettings: GridSettings,
  isEndTime: boolean = false
): string {
  const dayIndex = getDayIndexFromX(xPx, colWidth);
  const xWithinDay = xPx - (dayIndex * colWidth);

  let minutesInWindow = getMinutesInWindowFromX(xWithinDay, colWidth, gridSettings);

  // For end times at day boundary, snap to day_end
  if (isEndTime) {
    const workWindowMinutes = getWorkWindowMinutes(gridSettings);
    if (minutesInWindow >= workWindowMinutes - gridSettings.snapMinutes) {
      minutesInWindow = workWindowMinutes;
    }
  }

  return gridToDateTime(dayIndex, minutesInWindow, weekStart, gridSettings);
}

/**
 * Converts an ISO datetime to X pixel position.
 *
 * @param isoString - ISO datetime string
 * @param weekStart - Week start date
 * @param colWidth - Width of each day column
 * @param gridSettings - Grid settings
 */
export function dateTimeToPixel(
  isoString: string,
  weekStart: Date,
  colWidth: number,
  gridSettings: GridSettings
): number {
  const gridPos = dateTimeToGrid(isoString, weekStart, gridSettings);
  const workWindowMinutes = getWorkWindowMinutes(gridSettings);

  const dayOffset = gridPos.dayIndex * colWidth;
  const windowRatio = Math.max(0, Math.min(1, gridPos.minutesInWindow / workWindowMinutes));
  const xWithinDay = windowRatio * colWidth;

  return dayOffset + xWithinDay;
}

/**
 * Calculates width in pixels for a shift spanning from start to end.
 */
export function getShiftWidthPixels(
  startIso: string,
  endIso: string,
  weekStart: Date,
  colWidth: number,
  gridSettings: GridSettings
): { left: number; width: number; startDayIndex: number; endDayIndex: number } {
  const startPos = dateTimeToGrid(startIso, weekStart, gridSettings);
  const endPos = dateTimeToGrid(endIso, weekStart, gridSettings);
  const workWindowMinutes = getWorkWindowMinutes(gridSettings);

  const startX = dateTimeToPixel(startIso, weekStart, colWidth, gridSettings);
  const endX = dateTimeToPixel(endIso, weekStart, colWidth, gridSettings);

  // Handle multi-day: if end is on a later day, calculate full width
  if (endPos.dayIndex > startPos.dayIndex) {
    // Start day: from start to end of work window
    const startDayEndX = (startPos.dayIndex + 1) * colWidth;
    let totalWidth = startDayEndX - startX;

    // Full days in between
    for (let d = startPos.dayIndex + 1; d < endPos.dayIndex; d++) {
      totalWidth += colWidth;
    }

    // End day: from start of work window to end position
    const endDayStartX = endPos.dayIndex * colWidth;
    totalWidth += endX - endDayStartX;

    return {
      left: startX,
      width: Math.max(totalWidth, colWidth * 0.1), // Min width
      startDayIndex: startPos.dayIndex,
      endDayIndex: endPos.dayIndex,
    };
  }

  // Same day shift
  return {
    left: startX,
    width: Math.max(endX - startX, colWidth * 0.1),
    startDayIndex: startPos.dayIndex,
    endDayIndex: endPos.dayIndex,
  };
}

/**
 * Calculates new end datetime from resize operation.
 * IMPORTANT: Uses endX directly, NOT start + delta.
 *
 * @param endXPx - End X position in pixels
 * @param colWidth - Width of each day column
 * @param weekStart - Week start date
 * @param gridSettings - Grid settings
 * @param minDurationMinutes - Minimum shift duration
 * @param startIso - Start datetime (for minimum duration check)
 */
export function calculateResizeEnd(
  endXPx: number,
  colWidth: number,
  weekStart: Date,
  gridSettings: GridSettings,
  minDurationMinutes: number,
  startIso: string
): string {
  // Get end datetime from X position
  const endDateTime = pixelToDateTime(endXPx, colWidth, weekStart, gridSettings, true);

  // Validate minimum duration
  const startDate = new Date(startIso);
  const endDate = new Date(endDateTime);

  const durationMs = endDate.getTime() - startDate.getTime();
  const durationMinutes = durationMs / (1000 * 60);

  if (durationMinutes < minDurationMinutes) {
    // Enforce minimum duration
    const minEndDate = new Date(startDate.getTime() + minDurationMinutes * 60 * 1000);
    return formatLocalDateTime(minEndDate);
  }

  return endDateTime;
}

/**
 * Formats a Date to ISO datetime string WITH timezone offset.
 * This ensures the backend correctly interprets the local time.
 * Example: "2024-12-23T10:00:00+01:00" for CET
 */
function formatLocalDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const mins = date.getMinutes().toString().padStart(2, '0');

  // Get timezone offset in minutes (negative for UTC+, positive for UTC-)
  const tzOffset = -date.getTimezoneOffset();
  const tzSign = tzOffset >= 0 ? '+' : '-';
  const tzHours = Math.floor(Math.abs(tzOffset) / 60).toString().padStart(2, '0');
  const tzMins = (Math.abs(tzOffset) % 60).toString().padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${mins}:00${tzSign}${tzHours}:${tzMins}`;
}

/**
 * Gets X position from clientX, accounting for container rect and scroll.
 */
export function clientXToGridX(
  clientX: number,
  containerRect: DOMRect,
  scrollLeft: number
): number {
  return (clientX - containerRect.left) + scrollLeft;
}

/**
 * Calculates start and end times for a FULL_DAY drop.
 */
export function getFullDayTimes(
  dayIndex: number,
  weekStart: Date,
  gridSettings: GridSettings
): { startIso: string; endIso: string } {
  const startIso = gridToDateTime(dayIndex, 0, weekStart, gridSettings);
  const workWindowMinutes = getWorkWindowMinutes(gridSettings);
  const endIso = gridToDateTime(dayIndex, workWindowMinutes, weekStart, gridSettings);
  return { startIso, endIso };
}

/**
 * Debug helper: formats grid position for display.
 */
export function formatGridPosition(
  dayIndex: number,
  minutesInWindow: number,
  gridSettings: GridSettings
): string {
  const dayNames = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const totalMinutes = gridSettings.dayStartMinutes + minutesInWindow;
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  return `${dayNames[dayIndex]} ${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}
